/**
 * Bento (asymmetric) services grid — Yön A.
 * Featured (Business VoIP) spans 2 columns + has a UI mockup.
 * Other services use varied sizes for visual rhythm.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowUpRight, Check, Sparkles } from 'lucide-react';
import { SERVICES } from '../data/mock';

const PRICE_HINTS = {
  'business-voip': { from: '£11/user/mo', badge: 'Most popular' },
  'sip-trunk': { from: '£5/channel/mo' },
  'microsoft-teams': { from: '£8/user/mo' },
  'inbound': { from: '£24/agent/mo' },
  'outbound': { from: '£32/agent/mo' },
  'web-phone': { from: 'Included' },
  'video': { from: 'Included' },
  'unified-comms': { from: '£14/user/mo' },
};

function Icon({ name, size = 22, className = '' }) {
  const Ic = Icons[name] || Icons.Sparkles;
  return <Ic size={size} className={className} />;
}

export default function ServicesGridBento({ items, eyebrow, title, subtitle }) {
  // When the admin v2 page editor provides items, use them; otherwise fall
  // back to the legacy SERVICES mock so the bento always renders.
  const source = Array.isArray(items) && items.length ? items : SERVICES;
  const featured = source[0];
  const rest = source.slice(1, 9); // 8 cards in the bento

  return (
    <section className="bg-gradient-to-b from-white to-violet-50/30">
      <div className="container-x section-pad">
        <div className="text-center mb-12">
          <span className="eyebrow">What we do</span>
          <h2 className="text-3xl md:text-5xl font-extrabold mt-3 leading-tight">
            One platform, every <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-fuchsia-600">communication channel</span>
          </h2>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-5 grid-rows-[auto_auto_auto] md:grid-rows-[260px_260px_auto_auto] md:auto-rows-[260px]">
          {/* ROW 1+2 — featured (6) + two stacked compacts (3) + tall (3) */}
          <FeaturedCard service={featured} />

          {/* Two stacked compact cards (col 7-9) */}
          <div className="col-span-12 md:col-span-3 md:row-span-2 grid grid-rows-2 gap-4 md:gap-5">
            <BentoCard service={rest[0]} accent="emerald" compact />
            <BentoCard service={rest[1]} accent="sky" compact />
          </div>

          {/* Tall card (col 10-12, spans 2 rows) */}
          <BentoCard service={rest[2]} accent="rose" tall />

          {/* ROW 3 — 3 normal cards */}
          <BentoCard service={rest[3]} accent="amber" />
          <BentoCard service={rest[4]} accent="violet" />
          <BentoCard service={rest[5]} accent="indigo" />

          {/* ROW 4 — wide CTA + small */}
          {rest[6] && <BentoCard service={rest[6]} accent="fuchsia" wide />}
          {rest[7] && <BentoCard service={rest[7]} accent="teal" />}
        </div>
      </div>
    </section>
  );
}

function FeaturedCard({ service }) {
  if (!service) return null;
  const hint = PRICE_HINTS[service.slug] || {};
  const features = Array.isArray(service.features) ? service.features : [];
  return (
    <Link
      to={service.href || '#'}
      className="col-span-12 md:col-span-6 md:row-span-2 relative group rounded-3xl p-7 md:p-8 bg-gradient-to-br from-violet-600 via-violet-700 to-fuchsia-700 text-white overflow-hidden"
      data-testid="bento-featured"
    >
      {/* Decorative blobs */}
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-fuchsia-400/30 blur-3xl group-hover:scale-110 transition-transform" />
      <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-violet-400/20 blur-3xl" />

      {hint.badge && (
        <span className="absolute top-6 right-6 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400 text-amber-950 text-xs font-extrabold uppercase tracking-wider shadow-lg">
          <Sparkles size={11} /> {hint.badge}
        </span>
      )}

      <div className="relative z-10 h-full flex flex-col">
        <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center mb-5 shadow-xl">
          <Icon name={service.icon} size={28} className="text-white" />
        </div>
        <h3 className="text-2xl md:text-3xl font-extrabold leading-tight mb-3">{service.title}</h3>
        <p className="text-white/85 text-base leading-relaxed mb-5 max-w-md">{service.short}</p>

        {features.length > 0 && (
          <ul className="space-y-2 mb-6">
            {features.slice(0, 4).map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-white/90">
                <Check size={14} className="text-fuchsia-200" /> {f}
              </li>
            ))}
          </ul>
        )}

        {/* Mini "UI" mockup bar */}
        <div className="mt-auto bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-white/60 uppercase tracking-wider">Live dashboard</span>
            <span className="flex items-center gap-1 text-[10px]"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> 24 active calls</span>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2"><div className="h-1.5 flex-1 bg-white/15 rounded-full overflow-hidden"><div className="h-full w-3/4 bg-fuchsia-300 rounded-full" /></div><span className="text-[10px] text-white/70 w-8">75%</span></div>
            <div className="flex items-center gap-2"><div className="h-1.5 flex-1 bg-white/15 rounded-full overflow-hidden"><div className="h-full w-1/2 bg-violet-300 rounded-full" /></div><span className="text-[10px] text-white/70 w-8">52%</span></div>
            <div className="flex items-center gap-2"><div className="h-1.5 flex-1 bg-white/15 rounded-full overflow-hidden"><div className="h-full w-5/6 bg-amber-300 rounded-full" /></div><span className="text-[10px] text-white/70 w-8">88%</span></div>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between">
          <span className="text-xs text-white/70">From <strong className="text-white">{hint.from}</strong></span>
          <span className="inline-flex items-center gap-1 text-sm font-bold group-hover:gap-2 transition-all">
            Explore <ArrowUpRight size={16} />
          </span>
        </div>
      </div>
    </Link>
  );
}

const ACCENTS = {
  emerald: { bg: 'from-emerald-50 to-white', icon: 'bg-emerald-500', text: 'text-emerald-700', ring: 'group-hover:ring-emerald-300' },
  sky:     { bg: 'from-sky-50 to-white',     icon: 'bg-sky-500',     text: 'text-sky-700',     ring: 'group-hover:ring-sky-300' },
  rose:    { bg: 'from-rose-50 to-white',    icon: 'bg-rose-500',    text: 'text-rose-700',    ring: 'group-hover:ring-rose-300' },
  amber:   { bg: 'from-amber-50 to-white',   icon: 'bg-amber-500',   text: 'text-amber-700',   ring: 'group-hover:ring-amber-300' },
  violet:  { bg: 'from-violet-50 to-white',  icon: 'bg-violet-500',  text: 'text-violet-700',  ring: 'group-hover:ring-violet-300' },
  indigo:  { bg: 'from-indigo-50 to-white',  icon: 'bg-indigo-500',  text: 'text-indigo-700',  ring: 'group-hover:ring-indigo-300' },
  fuchsia: { bg: 'from-fuchsia-50 to-white', icon: 'bg-fuchsia-500', text: 'text-fuchsia-700', ring: 'group-hover:ring-fuchsia-300' },
  teal:    { bg: 'from-teal-50 to-white',    icon: 'bg-teal-500',    text: 'text-teal-700',    ring: 'group-hover:ring-teal-300' },
};

function BentoCard({ service, accent = 'violet', tall = false, compact = false, wide = false }) {
  if (!service) return null;
  const a = ACCENTS[accent];
  const hint = PRICE_HINTS[service.slug] || {};
  const features = Array.isArray(service.features) ? service.features : [];
  const colSpan = wide ? 'md:col-span-8' : tall ? 'md:col-span-3' : 'md:col-span-4';
  const rowSpan = tall ? 'md:row-span-2' : '';
  return (
    <Link
      to={service.href || '#'}
      className={`col-span-12 ${colSpan} ${rowSpan} group relative rounded-2xl bg-gradient-to-br ${a.bg} border border-slate-200/70 ring-1 ring-transparent ${a.ring} hover:-translate-y-1 hover:shadow-xl transition-all p-6 overflow-hidden`}
    >
      {hint.badge && !compact && (
        <span className="absolute top-4 right-4 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-extrabold uppercase tracking-wider">
          {hint.badge}
        </span>
      )}

      <div className={`w-11 h-11 rounded-xl ${a.icon} flex items-center justify-center text-white shadow-lg shadow-slate-900/10 mb-4`}>
        <Icon name={service.icon} size={20} />
      </div>

      <h3 className="font-extrabold text-slate-900 text-lg leading-tight mb-2">{service.title}</h3>
      <p className={`text-sm text-slate-600 ${compact ? 'line-clamp-2' : ''} mb-4 leading-relaxed`}>{service.short}</p>

      {!compact && tall && features.length > 0 && (
        <ul className="space-y-1.5 mb-4">
          {features.slice(0, 4).map((f) => (
            <li key={f} className="flex items-center gap-2 text-xs text-slate-700">
              <Check size={12} className={a.text} /> {f}
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-between mt-auto pt-2">
        {hint.from && <span className="text-xs text-slate-500">From <strong className="text-slate-900">{hint.from}</strong></span>}
        <span className={`inline-flex items-center gap-1 text-sm font-bold ${a.text} group-hover:gap-2 transition-all ml-auto`}>
          Learn more <ArrowUpRight size={14} />
        </span>
      </div>
    </Link>
  );
}
